import type {ReactElement, ReactNode} from 'react';
import React, {useMemo, useState} from 'react';
import type {SelectedImageAsset} from '@Components/ai-chat-window/ai-chat-window.types';
import {getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';
import {getUserImageThumbUrl} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import styles from './super-user-image-context-details.module.scss';
import type {SuperUserImageContextDetailsProps} from './super-user-image-context-details.types';

const getAssetUrl = (asset: SelectedImageAsset): string => {
  if (asset.assetType === 'model') {
    return getUrlForGeneratedAiChatModelAssetsImage(asset.hashedId, MediaItemSizeType.THUMB);
  }
  return getUserImageThumbUrl(asset.hashedId, asset.extension);
};

export function SuperUserImageContextDetails({enhancedPrompt, context, useSmartAssetSelection}: SuperUserImageContextDetailsProps): ReactElement | null {
  const [isExpanded, setIsExpanded] = useState(false);

  const summary = context?.summary?.trim() ?? '';
  const selectedAssets = context?.selectedAssets ?? [];
  const trimmedEnhancedPrompt = enhancedPrompt?.trim() ?? '';

  const smartAssetSelectionEnabled = useSmartAssetSelection === true;
  const hasContent = trimmedEnhancedPrompt.length > 0 || summary.length > 0 || selectedAssets.length > 0;

  const assetEntries = useMemo(
    () =>
      selectedAssets.map((asset, index) => ({
        key: `${asset.assetType}-${asset.hashedId}-${asset.messageId ?? 'no-msg'}-${index}`,
        url: getAssetUrl(asset),
        asset,
      })),
    [selectedAssets]
  );

  if (!hasContent) {
    return null;
  }

  const getSummary = (): ReactNode => {
    if (summary.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Context summary</span>
        <span className={styles.sectionText}>{summary}</span>
      </div>
    );
  };

  const getEnhancedPrompt = (): ReactNode => {
    if (trimmedEnhancedPrompt.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Enhanced prompt</span>
        <span className={styles.sectionText}>{trimmedEnhancedPrompt}</span>
      </div>
    );
  };

  const getAssetsToShow = (): ReactNode => {
    if (assetEntries.length === 0) {
      return null;
    }

    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Reference images ({assetEntries.length})</span>
        <div className={`flexbox gap-2 ${styles.assets}`}>
          {assetEntries.map(({key, url, asset}) => (
            <a key={key} href={url} target="_blank" rel="noreferrer" title={`${asset.assetType}: ${asset.hashedId}`}>
              <img src={url} alt="" aria-hidden="true" className={`radius-6 ${styles.assetThumb}`} />
            </a>
          ))}
        </div>
      </div>
    );
  };

  const getSmartAssetSelectionFlag = (): ReactNode => {
    return (
      <div className={'flex-v-row gap-1'}>
        <span className={styles.sectionLabel}>Smart asset selection</span>
        <span className={styles.sectionText}>{smartAssetSelectionEnabled ? 'On' : 'Off'}</span>
      </div>
    );
  };

  const getGenerationContext = (): ReactNode => {
    if (!isExpanded) {
      return null;
    }

    return (
      <div className={`flex-v-row gap-3 spacing-m-t-2`}>
        {getSmartAssetSelectionFlag()}
        {getSummary()}
        {getEnhancedPrompt()}
        {getAssetsToShow()}
      </div>
    );
  };

  return (
    <div className={`radius-8 spacing-p-2 spacing-m-t-2 ${styles.container}`}>
      <button
        type="button"
        className={`_cursor-pointer flexbox flex-align-items-center spacing-p-0 gap-1 ${styles.toggle}`}
        onClick={(): void => {
          setIsExpanded((prev) => !prev);
        }}
        aria-expanded={isExpanded}
      >
        <span className={`${styles.chevron} ${isExpanded ? styles.chevronExpanded : ''}`}>{'▶'}</span>
        <span>Generation Context</span>
      </button>
      {getGenerationContext()}
    </div>
  );
}
